import { useParticipants } from '@gripp/shared-logic';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, Text } from 'react-native';
import { Colors } from '../../themes/colors';
import { themedStyleSheet } from '../../themes/themedStyleSheet';
import { Icon } from '../crop-circle/icons';
import { spacing, tokens } from '../crop-circle/tokens';
import { ParticipantsSection } from './participantsSection';

export type ParticipantsDisclosureProps = {
  workspaceAssetId: string;
  /** The asset's own workspace name — see `ParticipantsSection`'s doc comment. */
  workspaceName?: string;
  /** Withholds the query until the band itself is open. Defaults to true. */
  enabled?: boolean;
  testID?: string;
};

/**
 * Participants, collapsed by default behind `Participants · <count>` — the
 * same collapsed treatment the gripp-os reference gives its own invite
 * section (`InviteParticipants.tsx`'s "Invite from another organization"
 * disclosure). The label's count comes off the first page, so the query runs
 * as soon as `enabled` regardless of the local expanded state.
 */
export const ParticipantsDisclosure = ({
  workspaceAssetId,
  workspaceName,
  enabled = true,
  testID,
}: ParticipantsDisclosureProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const {
    items,
    count,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useParticipants({ workspaceAssetId, enabled });

  // The expanded list has no "load more" control, so paging to completion is
  // this component's job — but only once expanded. The collapsed label needs
  // the first page alone (`count` is the first page's total), and the audience
  // can be large.
  useEffect(() => {
    if (enabled && expanded && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [enabled, expanded, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const label =
    count === undefined
      ? t('chat.participants.title')
      : t('chat.participants.titleWithCount', { count });

  return (
    <>
      <Pressable
        style={styles.header}
        onPress={() => setExpanded((value) => !value)}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={label}
        testID={testID ? `${testID}-toggle` : undefined}
      >
        <Text style={styles.label} numberOfLines={1}>
          {label}
        </Text>
        <Icon
          name={expanded ? 'Chevron Up' : 'Chevron Down'}
          size={16}
          color={Colors.role.textSoft}
        />
      </Pressable>
      {expanded && (
        <ScrollView style={styles.content} testID={testID}>
          <ParticipantsSection
            items={items}
            workspaceName={workspaceName}
            isLoading={isLoading}
            isError={isError}
            testID={testID ? `${testID}-list` : undefined}
          />
        </ScrollView>
      )}
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.xs,
  },
  label: {
    ...tokens.typography.label.mediumEmphasised,
    color: role.textStrong,
  },
  // Caps how far a large audience can push the feed down — the band stays
  // "inline beneath the header", not a scroll hijack of the whole column.
  content: {
    maxHeight: 240,
    marginTop: spacing.xs,
  },
}));
